import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { Match } from '@/lib/matches';
import { formatMatchDateTime, formatMatchDate } from '@/lib/matches';

interface WorldCupGroupTableProps {
  group: string;
  matches: Match[];
  onClick?: (match: Match) => void;
  defaultOpen?: boolean;
  useUTC?: boolean;
}

export function WorldCupGroupTable({ group, matches, onClick, defaultOpen = true, useUTC }: WorldCupGroupTableProps) {
  const [open, setOpen] = useState(defaultOpen);

  if (matches.length === 0) return null;

  const sorted = [...matches].sort(
    (a, b) => new Date(a.utcDate).getTime() - new Date(b.utcDate).getTime()
  );

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-bg-elevated">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-bg-hover transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-accent">Group {group}</span>
          <span className="text-[11px] text-text-muted tabular-nums">
            {matches.length} match{matches.length !== 1 ? 'es' : ''}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-text-muted border-b border-border">
                <th className="text-left py-2 pr-2 font-medium">Date</th>
                <th className="text-right py-2 px-2 font-medium">Home</th>
                <th className="text-center py-2 px-2 font-medium w-12">Score</th>
                <th className="text-left py-2 pl-2 font-medium">Away</th>
                <th className="text-right py-2 pl-2 font-medium">Time</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((m) => {
                const homeCrest = m.homeTeam.crest;
                const awayCrest = m.awayTeam.crest;
                const hasScore = m.score.fullTime.home !== null;
                return (
                  <tr
                    key={m.id}
                    onClick={onClick ? () => onClick(m) : undefined}
                    className={`border-b border-border/50 hover:bg-bg-hover transition-colors ${
                      onClick ? 'cursor-pointer' : ''
                    }`}
                  >
                    <td className="py-2.5 pr-2 text-text-muted align-middle">
                      {formatMatchDate(m.utcDate)}
                    </td>
                    <td className="py-2.5 px-2 text-right align-middle">
                      <div className="flex items-center gap-1.5 justify-end">
                        <span className="font-medium truncate max-w-[80px]">{m.homeTeam.shortName || m.homeTeam.name}</span>
                        {homeCrest && <img src={homeCrest} alt="" className="w-4 h-4 object-contain flex-shrink-0" />}
                      </div>
                    </td>
                    <td className="py-2.5 px-2 text-center align-middle">
                      <span className={`font-bold tabular-nums ${hasScore ? '' : 'text-text-muted'}`}>
                        {hasScore ? `${m.score.fullTime.home}-${m.score.fullTime.away}` : 'vs'}
                      </span>
                    </td>
                    <td className="py-2.5 pl-2 align-middle">
                      <div className="flex items-center gap-1.5">
                        {awayCrest && <img src={awayCrest} alt="" className="w-4 h-4 object-contain flex-shrink-0" />}
                        <span className="font-medium truncate max-w-[80px]">{m.awayTeam.shortName || m.awayTeam.name}</span>
                      </div>
                    </td>
                    <td className="py-2.5 pl-2 text-right text-text-muted align-middle whitespace-nowrap">
                      {formatMatchDateTime(m.utcDate, !!useUTC)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
